/* ============================================================
   SHOPratik · fab5 theme — theme.js
   One IIFE, vanilla ES6, no dependencies. Loaded with `defer`.
   Implements the §7 hook contract. Every feature is defensive:
   pages missing a hook simply skip that feature — never throws.
   ============================================================ */
(function () {
  'use strict';

  var S = window.SHOPRATIK = window.SHOPRATIK || {};
  var doc = document;
  var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var SPRING = 'cubic-bezier(.34,1.56,.64,1)';
  var CONF_COLORS = ['#ff5a5f', '#ffb703', '#3a86ff', '#2ec4b6', '#9b5de5', '#ff7ad5'];

  /* ---------------- tiny utils ---------------- */
  function qs(sel, root) { return (root || doc).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || doc).querySelectorAll(sel)); }
  function closest(el, sel) { return el && el.closest ? el.closest(sel) : null; }
  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function safe(fn) { try { fn(); } catch (err) { /* never break the page */ if (window.console && console.warn) console.warn('SHOPratik:', err); } }
  function imgSize(url, width) {
    if (!url || typeof url !== 'string') return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /* ---------------- money formatter ---------------- */
  function delimited(n, precision, thousands, decimal) {
    var neg = n < 0;
    var fixed = Math.abs(n).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return (neg ? '-' : '') + whole + (parts[1] ? decimal + parts[1] : '');
  }
  function formatMoney(cents, format) {
    var fmt = format || S.moneyFormat || '${{amount}}';
    if (typeof fmt !== 'string') fmt = '${{amount}}';
    var n = (parseFloat(cents) || 0) / 100;
    var m = /\{\{\s*(\w+)\s*\}\}/.exec(fmt);
    var key = m ? m[1] : 'amount';
    var value;
    switch (key) {
      case 'amount_no_decimals': value = delimited(n, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = delimited(n, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delimited(n, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = delimited(n, 2, "'", '.'); break;
      default: value = delimited(n, 2, ',', '.');
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value).replace(/<[^>]*>/g, '').trim();
  }

  /* ---------------- Cart API ---------------- */
  var CART_ADD = (S.cartAddUrl || '/cart/add') + '.js';
  var CART_CHANGE = (S.cartChangeUrl || '/cart/change') + '.js';
  var lastCart = null;

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('cart fetch failed');
      return r.json();
    }).then(function (cart) { lastCart = cart; return cart; });
  }
  function addToCart(formData) {
    return fetch(CART_ADD, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (body) {
        if (!r.ok) {
          var e = new Error('add failed');
          e.status = r.status;
          e.body = body;
          throw e;
        }
        return body;
      });
    });
  }
  function changeLine(line, qty) {
    return fetch(CART_CHANGE, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ line: line, quantity: qty })
    }).then(function (r) {
      if (!r.ok) throw new Error('change failed');
      return r.json();
    }).then(function (cart) { lastCart = cart; return cart; });
  }
  function updateCounts(cart) {
    if (!cart) return;
    qsa('[data-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
  }
  function withCart(fn) {
    getCart().then(fn).catch(function () { if (lastCart) fn(lastCart); });
  }

  /* ---------------- toast ---------------- */
  var toastTimer = null;
  function toast(msg) {
    var el = doc.getElementById('Toast');
    if (!el) {
      el = doc.createElement('div');
      el.id = 'Toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      doc.body.appendChild(el);
    }
    el.textContent = msg;
    el.classList.remove('show');
    void el.offsetWidth; /* restart transition */
    el.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('show'); }, 2600);
  }

  /* ---------------- confetti (v7c burst) ---------------- */
  function confetti(x, y) {
    if (reduce || !doc.body) return;
    var box = doc.getElementById('Confetti');
    if (!box) {
      box = doc.createElement('div');
      box.id = 'Confetti';
      box.setAttribute('aria-hidden', 'true');
      doc.body.appendChild(box);
    }
    for (var i = 0; i < 26; i++) {
      var c = doc.createElement('span');
      c.className = 'conf';
      var ang = Math.random() * Math.PI * 2;
      var dist = 60 + Math.random() * 120;
      c.style.left = x + 'px';
      c.style.top = y + 'px';
      c.style.background = CONF_COLORS[i % CONF_COLORS.length];
      c.style.setProperty('--dx', (Math.cos(ang) * dist).toFixed(0) + 'px');
      c.style.setProperty('--dy', (Math.sin(ang) * dist - 40).toFixed(0) + 'px');
      c.style.setProperty('--rot', (Math.random() * 720 - 360).toFixed(0) + 'deg');
      c.style.animationDelay = (Math.random() * 60).toFixed(0) + 'ms';
      if (Math.random() > .5) c.style.borderRadius = '50%';
      box.appendChild(c);
      (function (el) { setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 1000); })(c);
    }
  }

  /* ---------------- cart drawer ---------------- */
  var drawer = doc.getElementById('CartDrawer');
  var lastFocus = null;

  function openDrawer() {
    if (!drawer) return;
    lastFocus = doc.activeElement;
    drawer.hidden = false;
    void drawer.offsetWidth; /* let [hidden] removal paint before transition */
    drawer.classList.add('open');
    doc.body.classList.add('drawer-open');
    var panel = qs('.drawer__panel', drawer);
    if (panel && panel.focus) panel.focus();
  }
  function closeDrawer() {
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('open');
    doc.body.classList.remove('drawer-open');
    setTimeout(function () { drawer.hidden = true; }, reduce ? 0 : 380);
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (err) {} }
  }

  function dciHTML(item, line) {
    var title = item.product_title || item.title || '';
    var url = item.url || '#';
    var linePrice = item.final_line_price != null ? item.final_line_price : item.line_price;
    var img = item.image
      ? '<img src="' + esc(imgSize(item.image, 128)) + '" alt="' + esc(title) + '" loading="lazy" width="64" height="64">'
      : '<div class="dci__ph" aria-hidden="true"></div>';
    var variant = (item.variant_title && item.variant_title !== 'Default Title')
      ? '<span class="dci__var">' + esc(item.variant_title) + '</span>' : '';
    return '<div class="dci" data-line="' + line + '">' +
      '<a class="dci__img" href="' + esc(url) + '" tabindex="-1" aria-hidden="true">' + img + '</a>' +
      '<div class="dci__info">' +
        '<a class="dci__title" href="' + esc(url) + '">' + esc(title) + '</a>' +
        variant +
        '<span class="dci__price">' + esc(formatMoney(linePrice)) + '</span>' +
        '<div class="dci__row">' +
          '<div class="qty">' +
            '<button type="button" data-qty-minus aria-label="Decrease quantity">&minus;</button>' +
            '<input type="number" data-qty-input min="1" inputmode="numeric" value="' + (parseInt(item.quantity, 10) || 1) + '" aria-label="Quantity for ' + esc(title) + '">' +
            '<button type="button" data-qty-plus aria-label="Increase quantity">+</button>' +
          '</div>' +
          '<button type="button" class="dci__rm" data-remove>Remove</button>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  function renderDrawer(cart) {
    if (!cart) return;
    lastCart = cart;
    updateCounts(cart);
    var wrap = drawer ? qs('[data-drawer-items]', drawer) : qs('[data-drawer-items]');
    if (wrap) {
      if (!cart.items || !cart.items.length) {
        wrap.innerHTML = '<p class="drawer__empty">Your cart is empty.<br>Add some clever things &#10024;</p>';
      } else {
        wrap.innerHTML = cart.items.map(function (item, i) { return dciHTML(item, i + 1); }).join('');
      }
    }
    qsa('[data-drawer-subtotal]').forEach(function (el) { el.textContent = formatMoney(cart.total_price); });
  }

  /* ---------------- WhatsApp cart composer ---------------- */
  function waNumber() { return String(S.whatsapp || '').replace(/[^0-9]/g, ''); }
  function waCompose(cart) {
    var num = waNumber();
    if (!num || !cart) return;
    var lines = ["Hi ShopRatik! I'd like to order:"];
    (cart.items || []).forEach(function (item) {
      var linePrice = item.final_line_price != null ? item.final_line_price : item.line_price;
      lines.push('• ' + item.quantity + ' × ' + (item.product_title || item.title || 'Item') + ' (' + formatMoney(linePrice) + ')');
    });
    lines.push('Total: ' + formatMoney(cart.total_price));
    lines.push('(Cash on Delivery)');
    var url = 'https://wa.me/' + num + '?text=' + encodeURIComponent(lines.join('\n'));
    window.open(url, '_blank', 'noopener');
  }

  /* ---------------- drawer events ---------------- */
  function initDrawer() {
    if (!drawer) return;
    drawer.addEventListener('click', function (e) {
      if (closest(e.target, '[data-drawer-close]')) { e.preventDefault(); closeDrawer(); return; }
      if (closest(e.target, '[data-drawer-checkout]')) { e.preventDefault(); window.location = '/checkout'; return; }
      var wa = closest(e.target, '[data-drawer-wa]');
      if (wa) {
        e.preventDefault();
        if (waNumber()) withCart(waCompose);
        return;
      }
      var rm = closest(e.target, '[data-remove]');
      if (rm) {
        e.preventDefault();
        var row = closest(rm, '[data-line]');
        var line = row ? parseInt(row.getAttribute('data-line'), 10) : 0;
        if (line) {
          changeLine(line, 0).then(renderDrawer).catch(function () { toast('Hmm, that didn’t work — try again 🙈'); });
        }
      }
    });
    drawer.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || !input.hasAttribute || !input.hasAttribute('data-qty-input')) return;
      var row = closest(input, '[data-line]');
      var line = row ? parseInt(row.getAttribute('data-line'), 10) : 0;
      if (!line) return;
      var qty = Math.max(1, parseInt(input.value, 10) || 1);
      input.value = qty;
      changeLine(line, qty).then(renderDrawer).catch(function () { toast('Hmm, that didn’t work — try again 🙈'); });
    });
  }

  /* cart toggle — anywhere on the page (header, footer) */
  function initCartToggle() {
    doc.addEventListener('click', function (e) {
      var t = closest(e.target, '[data-cart-toggle]');
      if (!t || !drawer) return; /* no drawer on page → let links behave normally */
      e.preventDefault();
      getCart().then(function (cart) { renderDrawer(cart); openDrawer(); })
        .catch(function () { openDrawer(); });
    });
    doc.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer && !drawer.hidden) closeDrawer();
    });
  }

  /* ---------------- AJAX add-to-cart ---------------- */
  function initAjaxAdd() {
    doc.addEventListener('submit', function (e) {
      var form = closest(e.target, 'form[data-ajax-add]');
      if (!form) return;
      if (!window.fetch || !window.FormData) return; /* graceful: native submit */
      e.preventDefault();
      var btn = form.querySelector('[type="submit"]');
      var fd = new FormData(form);
      if (btn) btn.disabled = true;
      var release = function () { if (btn) btn.disabled = false; };
      addToCart(fd)
        .then(getCart)
        .then(function (cart) {
          release();
          renderDrawer(cart);
          if (btn) {
            var r = btn.getBoundingClientRect();
            confetti(r.left + r.width / 2, r.top + r.height / 2);
            if (btn.animate && !reduce) {
              btn.animate(
                [{ transform: 'scale(1)' }, { transform: 'scale(.86)' }, { transform: 'scale(1.12)' }, { transform: 'scale(1)' }],
                { duration: 340, easing: SPRING }
              );
            }
          }
          openDrawer();
          toast('Added ✓ شو براتيك!');
        })
        .catch(function (err) {
          release();
          if (err && err.status === 422 && err.body && err.body.description) {
            toast(err.body.description); /* e.g. sold out / max in cart */
          } else {
            /* network / unexpected → fall back to native submit */
            try { form.removeAttribute('data-ajax-add'); form.submit(); } catch (e2) {}
          }
        });
    });
  }

  /* ---------------- cart PAGE line bindings ---------------- */
  function initCartPage() {
    var rows = qsa('[data-cart-line]');
    rows.forEach(function (row) {
      var line = parseInt(row.getAttribute('data-line'), 10);
      if (!line) return;
      row.addEventListener('change', function (e) {
        var input = e.target;
        if (!input || !input.hasAttribute || !input.hasAttribute('data-qty-input')) return;
        var qty = Math.max(1, parseInt(input.value, 10) || 1);
        input.value = qty;
        changeLine(line, qty).then(function () { window.location.reload(); })
          .catch(function () { toast('Couldn’t update — try again 🙈'); });
      });
      row.addEventListener('click', function (e) {
        var rm = closest(e.target, '[data-remove]');
        if (!rm || !row.contains(rm)) return;
        e.preventDefault();
        changeLine(line, 0).then(function () { window.location.reload(); })
          .catch(function () { toast('Couldn’t remove — try again 🙈'); });
      });
    });
    /* WhatsApp order from cart page */
    qsa('[data-cart-wa]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        if (!waNumber()) return;
        e.preventDefault();
        withCart(waCompose);
      });
    });
  }

  /* ---------------- qty steppers (global, clamp ≥1) ---------------- */
  function initSteppers() {
    doc.addEventListener('click', function (e) {
      var minus = closest(e.target, '[data-qty-minus]');
      var plus = closest(e.target, '[data-qty-plus]');
      var btn = minus || plus;
      if (!btn) return;
      var box = closest(btn, '.qty') || btn.parentElement;
      var input = box ? box.querySelector('[data-qty-input]') : null;
      if (!input) return;
      var v = parseInt(input.value, 10) || 1;
      v = minus ? v - 1 : v + 1;
      if (v < 1) v = 1;
      if (String(input.value) !== String(v)) {
        input.value = v;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    /* clamp manual typing */
    doc.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || !input.hasAttribute || !input.hasAttribute('data-qty-input')) return;
      var v = parseInt(input.value, 10);
      if (!v || v < 1) input.value = 1;
    });
  }

  /* ---------------- scroll reveal ---------------- */
  function initReveal() {
    var els = qsa('[data-reveal]');
    if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    /* auto-stagger: --d = index within parent, capped at 8 */
    var counts = new Map();
    els.forEach(function (el) {
      if (el.style.getPropertyValue('--d')) return; /* respect authored stagger */
      var p = el.parentElement || doc.body;
      if (p.classList && p.classList.contains('pcard-wrap') && p.parentElement) p = p.parentElement;
      var n = counts.get(p) || 0;
      el.style.setProperty('--d', Math.min(n, 8));
      counts.set(p, n + 1);
    });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var el = en.target;
        el.classList.add('in');
        io.unobserve(el);
        /* clear the stagger delay once revealed so hover springs stay snappy */
        var d = parseInt(el.style.getPropertyValue('--d'), 10) || 0;
        setTimeout(function () { el.style.setProperty('--d', 0); }, d * 60 + 700);
      });
    }, { threshold: .12, rootMargin: '0px 0px -40px 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------------- rail: drag-scroll + keyboard (v7a) ---------------- */
  function initRails() {
    qsa('[data-rail]').forEach(function (rail) {
      if (!rail.hasAttribute('tabindex')) rail.setAttribute('tabindex', '0');
      var down = false, startX = 0, startScroll = 0, moved = 0;
      rail.addEventListener('pointerdown', function (e) {
        if (closest(e.target, 'button, a, input, select, textarea')) return;
        down = true; moved = 0;
        startX = e.clientX; startScroll = rail.scrollLeft;
        rail.classList.add('dragging');
        if (rail.setPointerCapture) { try { rail.setPointerCapture(e.pointerId); } catch (err) {} }
      });
      rail.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        moved = Math.max(moved, Math.abs(dx));
        rail.scrollLeft = startScroll - dx;
      });
      function end() { down = false; rail.classList.remove('dragging'); }
      rail.addEventListener('pointerup', end);
      rail.addEventListener('pointercancel', end);
      rail.addEventListener('pointerleave', end);
      /* suppress accidental clicks after a real drag (>6px) */
      rail.addEventListener('click', function (e) {
        if (moved > 6) { e.preventDefault(); e.stopPropagation(); moved = 0; }
      }, true);
      rail.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        var delta = e.key === 'ArrowRight' ? 236 : -236;
        if (rail.scrollBy) rail.scrollBy({ left: delta, behavior: reduce ? 'auto' : 'smooth' });
        else rail.scrollLeft += delta;
      });
    });
  }

  /* ---------------- hero parallax (v7a apply() math) ---------------- */
  function initParallax() {
    if (reduce) return;
    qsa('[data-parallax-scene]').forEach(function (scene) {
      var layers = qsa('[data-depth]', scene);
      var floats = qsa('[data-parallax-float]', scene);
      if (!layers.length && !floats.length) return;
      var raf = null, tx = 0, ty = 0;
      function apply() {
        raf = null;
        layers.forEach(function (l) {
          var d = parseFloat(l.getAttribute('data-depth')) || 0;
          l.style.transform = 'translate(' + (tx * -34 * d * 6).toFixed(1) + 'px,' + (ty * -26 * d * 6).toFixed(1) + 'px)';
        });
        floats.forEach(function (f) {
          f.style.transform = 'translate(' + (tx * 18).toFixed(1) + 'px,' + (ty * 14).toFixed(1) + 'px)';
        });
      }
      scene.addEventListener('mousemove', function (e) {
        var r = scene.getBoundingClientRect();
        if (!r.width || !r.height) return;
        tx = (e.clientX - r.left) / r.width - .5;
        ty = (e.clientY - r.top) / r.height - .5;
        if (!raf) raf = requestAnimationFrame(apply);
      });
      scene.addEventListener('mouseleave', function () {
        tx = 0; ty = 0;
        if (!raf) raf = requestAnimationFrame(apply);
      });
    });
  }

  /* ---------------- shelf category filter ---------------- */
  function initShelfFilter() {
    var chips = qsa('[data-filter-chip]');
    var shelf = qs('[data-shelf-grid]');
    if (!chips.length || !shelf) return;
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        chips.forEach(function (c) {
          c.classList.remove('active');
          c.setAttribute('aria-pressed', 'false');
        });
        chip.classList.add('active');
        chip.setAttribute('aria-pressed', 'true');
        var cat = chip.getAttribute('data-cat') || 'All';
        qsa('.pcard-wrap', shelf).forEach(function (wrap) {
          var show = (cat === 'All') || (wrap.getAttribute('data-cat') === cat);
          wrap.style.display = show ? '' : 'none';
        });
      });
    });
  }

  /* ---------------- PDP gallery thumbs ---------------- */
  function initGallery() {
    var main = qs('[data-gallery-main]');
    if (!main) return;
    var mainImg = main.tagName === 'IMG' ? main : main.querySelector('img');
    var thumbs = qsa('[data-gallery-thumb]');
    if (!mainImg || !thumbs.length) return;
    thumbs.forEach(function (th) {
      th.addEventListener('click', function (e) {
        e.preventDefault();
        var inner = th.querySelector('img');
        var src = th.getAttribute('data-src') || th.getAttribute('href') || (inner && (inner.currentSrc || inner.src));
        var srcset = th.getAttribute('data-srcset');
        if (!src) return;
        mainImg.src = src;
        if (srcset) mainImg.setAttribute('srcset', srcset);
        else mainImg.removeAttribute('srcset');
        var alt = th.getAttribute('data-alt') || (inner && inner.alt);
        if (alt) mainImg.alt = alt;
        thumbs.forEach(function (t) { t.classList.remove('on'); });
        th.classList.add('on');
      });
    });
  }

  /* ---------------- PDP variant select ---------------- */
  function initVariants() {
    qsa('[data-variant-select]').forEach(function (sel) {
      var form = closest(sel, 'form');
      var scope = form || doc;
      var dataEl = scope.querySelector('script[data-variants]') || qs('script[data-variants]');
      if (!dataEl) return;
      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }
      if (!Array.isArray(variants)) return;
      var idInput = form ? form.querySelector('input[name="id"]') : null;
      var btn = form ? form.querySelector('[type="submit"]') : null;
      var priceEl = qs('[data-pdp-price]');
      if (btn && !btn.getAttribute('data-label')) btn.setAttribute('data-label', btn.textContent.trim());
      sel.addEventListener('change', function () {
        var v = null;
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === String(sel.value)) { v = variants[i]; break; }
        }
        if (!v) return;
        if (idInput) idInput.value = v.id;
        if (priceEl) priceEl.textContent = formatMoney(v.price);
        if (btn) {
          if (v.available) {
            btn.disabled = false;
            btn.textContent = btn.getAttribute('data-label') || 'Add to cart 🛒';
          } else {
            btn.disabled = true;
            btn.textContent = 'Sold out — restocking soon';
          }
        }
      });
    });
  }

  /* ---------------- hero wall clock (live local time) ---------------- */
  /* new Date() uses the visitor's own device timezone → shows THEIR local time.
     Hands are centered in CSS, so we only set a rotate(): 0deg = 12 o'clock, clockwise. */
  function initClock() {
    var clocks = qsa('[data-clock]');
    if (!clocks.length) return;
    var hands = clocks.map(function (c) {
      return { h: qs('[data-clock-h]', c), m: qs('[data-clock-m]', c), s: qs('[data-clock-s]', c) };
    });
    function tick() {
      var d = new Date();
      var sec = d.getSeconds();
      var min = d.getMinutes();
      var hr = d.getHours() % 12;
      var hDeg = hr * 30 + min * 0.5;        /* hour: 30°/hr + creep with minutes */
      var mDeg = min * 6 + sec * 0.1;        /* minute: 6°/min + smooth creep */
      var sDeg = sec * 6;                     /* second: 6°/sec */
      hands.forEach(function (hd) {
        if (hd.h) hd.h.style.transform = 'rotate(' + hDeg.toFixed(2) + 'deg)';
        if (hd.m) hd.m.style.transform = 'rotate(' + mDeg.toFixed(2) + 'deg)';
        if (hd.s && !reduce) hd.s.style.transform = 'rotate(' + sDeg + 'deg)';
      });
    }
    tick();
    setInterval(tick, reduce ? 30000 : 1000); /* reduced-motion: quiet minute updates only */
  }

  /* ---------------- boot ---------------- */
  function init() {
    safe(initClock);
    safe(initDrawer);
    safe(initCartToggle);
    safe(initAjaxAdd);
    safe(initCartPage);
    safe(initSteppers);
    safe(initReveal);
    safe(initRails);
    safe(initParallax);
    safe(initShelfFilter);
    safe(initGallery);
    safe(initVariants);
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();

  /* expose small helpers for sections that want them */
  S.formatMoney = formatMoney;
  S.toast = toast;
  S.confetti = confetti;
  S.getCart = getCart;
  S.openDrawer = openDrawer;
  S.closeDrawer = closeDrawer;
  S.renderDrawer = renderDrawer;
})();
